import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { FadeUp, Label, Emblem } from "@/components/Primitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GUIDE_URL = "/noir-psyche-the-psychology-they-never-taught-you.pdf";
const GUIDE_FILENAME = "Noir Psyche – The Psychology They Never Taught You.pdf";

const INSIDE = [
  { n: "01", title: "Cognitive Blind Spots", desc: "The mental shortcuts that quietly run your decisions before you're aware of them." },
  { n: "02", title: "Reading People", desc: "The micro-signals of trust, fear, and deception hiding in plain sight." },
  { n: "03", title: "The Shadow Self", desc: "How to integrate the parts of you that you've been trained to avoid." },
  { n: "04", title: "Emotional Regulation", desc: "Frameworks to stay grounded and clear-headed under real pressure." },
  { n: "05", title: "Influence & Defense", desc: "How persuasion actually works — and how to recognize it being used on you." },
];

export default function FreeGuide() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const navigate = useNavigate();

  const triggerDownload = () => {
    const a = document.createElement("a");
    a.href = GUIDE_URL;
    a.download = GUIDE_FILENAME;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        email,
        source: "free-guide",
      });
      setStatus("done");
      // Keep the instant PDF download, then send them to the Thank You page.
      triggerDownload();
      setTimeout(() => navigate("/thank-you"), 600);
    } catch (err) {
      setStatus("idle");
      const code = err?.response?.status;
      toast.error(
        code === 422
          ? "Please enter a valid email address."
          : "Something went wrong. Please try again."
      );
    }
  };

  return (
    <main data-testid="free-guide-page" className="relative pt-[72px]">
      {/* Hero / capture */}
      <section className="relative overflow-hidden">
        <div className="crimson-glow absolute -top-10 right-[6%] h-[460px] w-[460px] rounded-full opacity-70" />
        <div className="crimson-glow absolute bottom-0 left-[-8%] h-[360px] w-[360px] rounded-full opacity-50" />

        <div className="relative mx-auto max-w-[1100px] px-6 md:px-10 py-20 md:py-32 flex flex-col items-center text-center">
          <FadeUp>
            <div className="flex items-center justify-center gap-4 mb-8">
              <span className="h-px w-10" style={{ background: "var(--noir-crimson)" }} />
              <Label>The Free Guide</Label>
              <span className="h-px w-10" style={{ background: "var(--noir-crimson)" }} />
            </div>
          </FadeUp>

          <FadeUp delay={0.05}>
            <h1 className="font-display font-light leading-[0.9] tracking-tight text-5xl md:text-7xl lg:text-8xl">
              The Psychology <br />
              They <span className="text-crimson italic">Never</span> Taught You
            </h1>
          </FadeUp>

          <FadeUp delay={0.1}>
            <p className="mt-8 max-w-xl text-white/55 text-base md:text-lg leading-relaxed">
              A free field guide to the hidden mechanics of the human mind — the
              blind spots, the signals, and the frameworks most people spend a
              lifetime missing. Drop your email below and it's yours.
            </p>
          </FadeUp>

          {/* Branded capture form -> backend -> Beehiiv, centered below the headline */}
          <FadeUp delay={0.15} className="mt-12 w-full">
            <form
              onSubmit={submit}
              data-testid="free-guide-form"
              className="mx-auto w-full max-w-xl"
            >
              <div className="flex items-center gap-4 border-b border-white/20 pb-2 focus-within:border-[var(--noir-crimson)] transition-colors">
                <input
                  data-testid="free-guide-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  aria-label="Email address"
                  className="flex-1 bg-transparent py-3 text-lg md:text-xl text-white placeholder:text-white/30 outline-none font-display tracking-wide text-center"
                />
              </div>

              <motion.button
                data-testid="download-free-guide-btn"
                type="submit"
                disabled={status !== "idle"}
                whileTap={{ scale: 0.98 }}
                aria-label="Download Free Guide"
                className="group mt-8 flex w-full items-center justify-center gap-3 px-10 py-5 text-white font-mono-accent text-xs uppercase tracking-[0.25em] disabled:opacity-70 transition-transform"
                style={{ background: "var(--noir-crimson)" }}
              >
                {status === "loading" && <Loader2 size={18} className="animate-spin" />}
                {status === "done" && <Check size={18} />}
                {status === "idle" && (
                  <Download size={18} className="transition-transform group-hover:translate-y-0.5" />
                )}
                <span>{status === "done" ? "Sent to You" : "Download Free Guide"}</span>
              </motion.button>
            </form>
          </FadeUp>

          <FadeUp delay={0.2}>
            <p className="mt-6 font-mono-accent text-[10px] uppercase tracking-[0.2em] text-white/30">
              Free PDF · No spam · Only signal
            </p>
          </FadeUp>
        </div>
      </section>


      {/* What's inside */}
      <section
        data-testid="whats-inside"
        className="relative border-t border-white/8 bg-[#080808]"
      >
        <div className="mx-auto max-w-[1500px] px-6 md:px-10 py-24 md:py-32">
          <FadeUp className="mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <Label>What's Inside</Label>
              <h2 className="mt-4 font-display text-4xl md:text-6xl leading-[0.9] tracking-tight">
                Five <span className="text-crimson italic">Chapters</span> <br />
                of Clarity
              </h2>
            </div>
            <p className="max-w-sm text-white/50 text-sm md:text-base leading-relaxed">
              No fluff. Each chapter is a sharp, usable framework you can apply the
              moment you close the file.
            </p>
          </FadeUp>

          <div className="border-t border-white/8">
            {INSIDE.map((item, i) => (
              <FadeUp key={item.n} delay={i * 0.06}>
                <div
                  data-testid={`inside-${item.n}`}
                  className="group grid grid-cols-12 items-center gap-4 border-b border-white/8 py-8 md:py-9 transition-colors hover:bg-white/[0.02]"
                >
                  <span className="col-span-2 md:col-span-1 font-mono-accent text-sm text-white/30 group-hover:text-crimson transition-colors">
                    {item.n}
                  </span>
                  <h3 className="col-span-10 md:col-span-3 font-display text-2xl md:text-4xl tracking-tight transition-transform duration-500 group-hover:translate-x-2">
                    {item.title}
                  </h3>
                  <p className="col-span-12 md:col-span-8 mt-2 md:mt-0 text-white/50 text-sm md:text-base leading-relaxed md:pl-8">
                    {item.desc}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>

          {/* bottom CTA */}
          <FadeUp>
            <div className="mt-20 flex flex-col items-center text-center gap-6">
              <Emblem size={32} className="text-white/60" />
              <h3 className="font-display text-3xl md:text-5xl tracking-tight">
                Ready to <span className="text-crimson italic">see what others miss?</span>
              </h3>
              <button
                data-testid="scroll-to-form-btn"
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="flex items-center gap-3 px-10 py-5 text-white font-mono-accent text-xs uppercase tracking-[0.25em] transition-transform active:scale-[0.98]"
                style={{ background: "var(--noir-crimson)" }}
                aria-label="Get the free guide"
              >
                <Download size={18} />
                Get the Free Guide
              </button>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
